import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { ZodB2bUserFlag } from '@Common/types/bo/b2b/b2b';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { ZodRestrictionsFlags } from '@Common/types/bo/user_action_request';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodVulnerableCommentChoice } from '@Common/types/bo/vulnerable_comment_choices';
import { ZodUserInvestmentHorizon } from '@Common/types/bo_wm/customers/create_customer';
import { ZodUserInvestmentObjectives } from '@Common/types/bo_wm/customers/investment_objectives';
import { ZodOnboardingPath } from '@Common/types/flows/onboarding';
import { ZodFSUCases } from '@Common/types/profile/me';
import { ZodUserAccountType } from '@Common/types/questions/account_type';
import { ZodAllEmployerIndustries } from '@Common/types/questions/employer_industry';
import { ZodNatureOfTransactions } from '@Common/types/questions/nature_of_transactions';
import { ZodReasonOfOpeningAccount } from '@Common/types/questions/user_profile';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { ZodWealthManagerClientType } from '@Common/types/wealth_managers/client';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodHashedIdRequest } from '@Common/types/zod/zod-hash-id';
import { createZodHashIdParamsValidator } from '@Common/types/zod/zod-params';
import { ZodPhoneNumber } from '@Common/types/zod/zod-phone-number';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

import type { BOLogResource } from '@Common/types/bo/bo_logs';

export const KycStateValues = [
  'verified',
  'reported',
  'poa_not_verified',
  'screening_failed',
  'not_verified',
  'kyc_approved',
] as const;
export const ZodKycState = z.enum(KycStateValues);
export type KycStateT = (typeof KycStateValues)[number];

export const InvestmentAccountTypeValues = ['pea', 'gia', 'per'] as const;
export type InvestmentAccountTypeT = (typeof InvestmentAccountTypeValues)[number];

export const TransferInvestmentAccountTypeValues = ['gia', 'pea', 'b2b', 'per'] as const;
export const ZodTransferInvestmentAccountType = z.enum(TransferInvestmentAccountTypeValues);
export type TransferInvestmentAccountTypeT = (typeof TransferInvestmentAccountTypeValues)[number];

export const userRisks = ['1_low', '2_medium', '3_high', '4_suggested_rejection'] as const;
export const ZodUserRisk = z.enum(userRisks);
export type UserRiskT = (typeof userRisks)[number];

export const LegalEntityValues = ['sal', 'sfa', 'sda'] as const;
export const ZodLegalEntity = z.enum(LegalEntityValues);

export type LegalEntity = z.TypeOf<typeof ZodLegalEntity>;

export enum USER_RISK_BOUNDS {
  Low = 40,
  Medium = 61,
  High = 75,
  SuggestedRejection = 100,
}

export enum INVESTMENT_EXPERIENCE_SCORE_BOUNDS {
  Low = 40,
  Medium = 76,
  High = 100,
}

export const investmentExperienceScores = ['1_low', '2_medium', '3_high'] as const;
export const ZodInvestmentExperienceScore = z.enum(investmentExperienceScores);
export type InvestmentExperienceScore = (typeof investmentExperienceScores)[number];

export const MdlrVerificationStatusValues = [
  'verified',
  'declined',
  'exverified',
  'migrated',
  'referred',
  'reviewed',
  'unverified',
] as const;

const ZodMdlrVerificationStatus = z.enum(MdlrVerificationStatusValues);

export type MdlrVerificationStatus = z.TypeOf<typeof ZodMdlrVerificationStatus>;

export const ZodUserAccountTypeBo = ZodUserAccountType.or(ZodWealthManagerClientType);
export type UserAccountTypeBo = z.TypeOf<typeof ZodUserAccountTypeBo>;

export const UserAccountStatusValuesBo = ['active', 'inactive', 'closed', 'archived'] as const;

const ZodUserAccountStatusBo = z.enum(UserAccountStatusValuesBo);

export type UserAccountStatusBo = z.infer<typeof ZodUserAccountStatusBo>;

export const UserEckertStatusValues = ['not_activated', 'active', 'inactive'] as const;
export const ZodUserEckertStatus = z.enum(UserEckertStatusValues);
export type UserEckertStatus = z.infer<typeof ZodUserEckertStatus>;

export const ZodGetUserOneResponse = z.object({
  id: ZodId,
  email: ZodTrimmedString.nullable(),
  phone_number: ZodPhoneNumber.nullable(),
  username: ZodTrimmedString,
  display_name: ZodTrimmedString,
  firstname: ZodTrimmedString.nullable(),
  lastname: ZodTrimmedString.nullable(),
  gender: ZodTrimmedString.nullable(),
  country_of_tax_residence: ZodTrimmedString.nullable(),
  approbation_state: ZodTrimmedString.nullable(),
  kyc_state: ZodKycState,
  created_at: ZodDateTimeString,
  risk_score: z.number().nullable(),
  risk: ZodUserRisk.nullable(),
  onboarding_risk_score: z.number().nullable(),
  onboarding_risk: ZodUserRisk.nullable(),
  onboarding_path: ZodOnboardingPath.nullable(),
  restrictions_flags: ZodRestrictionsFlags,
  fsu: ZodFSUCases,
  is_ambassador: z.boolean(),
  is_politically_exposed: z.boolean().nullable(),
  social_url: ZodTrimmedString.nullable(),
  is_shares_staff: z.boolean(),
  entities: z.array(ZodLegalEntity),
  is_vulnerable: z.boolean(),
  birthdate: z.date().nullable(),
  account_type: ZodUserAccountTypeBo,
  mdlr_verification_status: ZodMdlrVerificationStatus.nullable(),
  account_status: ZodUserAccountStatusBo,
  eckert_status: ZodUserEckertStatus,
  company_name: ZodTrimmedString.nullable(),
  b2b_flags: z.array(ZodB2bUserFlag),
  vulnerable_comment_choices: z.array(ZodVulnerableCommentChoice),
  tenant_id: ZodId,
  wealth_manager_id: ZodId.nullable(),
});
export type GetUserOneResponseI = z.TypeOf<typeof ZodGetUserOneResponse>;

/**
 * GET /apibo/users
 */

const ZodGetUsersResponse = z.array(ZodGetUserOneResponse);
export type GetUsersResponseI = z.TypeOf<typeof ZodGetUsersResponse>;

export const getUsersRequestQuerySortColumns = [
  'id',
  'email',
  'phone_number',
  'username',
  'display_name',
  'firstname',
  'lastname',
  'gender',
  'country_of_tax_residence',
  'approbation_state',
  'kyc_state',
  'created_at',
  'risk_score',
  'onboarding_risk_score',
  'onboarding_path',
  'fsu',
  'is_ambassador',
  'is_politically_exposed',
  'is_shares_staff',
  'birthdate',
  'account_type',
  'account_status',
  'tenant_id',
] as const satisfies readonly (keyof GetUserOneResponseI)[];

export const ZodGetUsersRequestQueryFilter = z
  .object({
    query: ZodTrimmedString.optional(),
    id: createZodArrayable(ZodId).optional(),
    kyc_state: createZodArrayable(ZodTrimmedString).optional(),
    approbation_state: createZodArrayable(ZodTrimmedString).optional(),
    email: createZodArrayable(ZodTrimmedString).optional(),
    phone_number: createZodArrayable(ZodTrimmedString).optional(),
    username: createZodArrayable(ZodTrimmedString).optional(),
    display_name: createZodArrayable(ZodTrimmedString).optional(),
    firstname: createZodArrayable(ZodTrimmedString).optional(),
    lastname: createZodArrayable(ZodTrimmedString).optional(),
    gender: createZodArrayable(ZodTrimmedString).optional(),
    country_of_tax_residence: createZodArrayable(ZodTrimmedString).optional(),
    risk: createZodArrayable(ZodTrimmedString).optional(),
    onboarding_risk: createZodArrayable(ZodTrimmedString).optional(),
    onboarding_path: createZodArrayable(ZodTrimmedString).optional(),
    from_created_at: ZodDateTimeString.optional(),
    to_created_at: ZodDateTimeString.optional(),
    full_name: createZodArrayable(ZodTrimmedString).optional(),
    restrictions_flags: createZodArrayable(ZodTrimmedString).optional(),
    fsu: createZodArrayable(ZodFSUCases).optional(),
    is_ambassador: z.boolean().optional(),
    is_politically_exposed: z.boolean().optional(),
    social_url: createZodArrayable(ZodTrimmedString).optional(),
    is_shares_staff: z.boolean().optional(),
    entities: createZodArrayable(ZodLegalEntity).optional(),
    is_vulnerable: z.boolean().optional(),
    birthdate: ZodDateTimeString.optional(),
    account_type: ZodUserAccountTypeBo.optional(),
    wealth_manager_id: createZodArrayable(ZodId).optional(),
    mdlr_verification_status: createZodArrayable(ZodMdlrVerificationStatus).optional(),
    account_status: createZodArrayable(ZodUserAccountStatusBo).optional(),
    company_name: ZodTrimmedString.optional(),
    b2b_flags: createZodArrayable(ZodB2bUserFlag).optional(),
    vulnerable_comment_choices: createZodArrayable(ZodVulnerableCommentChoice).optional(),
    tenant_id: createZodArrayable(ZodId).optional(),
  })
  .partial();

const ZodGetUsersRequestQuery = buildZodBoListQueryParsed(
  ZodGetUsersRequestQueryFilter,
  getUsersRequestQuerySortColumns,
  { range: [0, 9] },
);

export type GetUsersRequestQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetUsersRequestQueryFilter>,
  (typeof getUsersRequestQuerySortColumns)[number]
>;

const ZodGetUserDetailsResponse = z.object({
  id: ZodId,
  account_type: ZodUserAccountTypeBo,
  al_account_id: ZodTrimmedString.nullable(),
  annual_income_range_max: z.number().int().nullable(),
  annual_income_range_min: z.number().int().nullable(),
  approbation_state: ZodTrimmedString.nullable(),
  bank_account_can_be_unlinked: z.boolean(),
  bank_account_holder_name: ZodTrimmedString.nullable(),
  bank_account_linked: z.boolean(),
  bank_account_number: ZodTrimmedString.nullable(),
  bank_name: ZodTrimmedString.nullable(),
  bio: ZodTrimmedString.nullable(),
  birthdate: z.number().nullable(),
  ca_search: z.object({ ca_id: ZodId, url: ZodTrimmedString }).optional(),
  city: ZodTrimmedString.nullable(),
  close_at: z.number().nullable(),
  country_of_tax_residence: ZodTrimmedString.nullable(),
  created_at: z.number().nullable(),
  currency: ZodCurrency,
  display_name: ZodTrimmedString,
  email_verified: z.boolean(),
  email: ZodTrimmedString.nullable(),
  email_state: z.enum(['blocked', 'permanently_blocked', 'invalid']).nullable(),
  employees: z.array(ZodId),
  employer_industry: ZodAllEmployerIndustries.nullable(),
  employment_status: ZodTrimmedString.nullable(),
  job_title: ZodTrimmedString.nullable(),
  entities: z.array(ZodLegalEntity),
  estimated_net_worth_range_max: z.number().int().nullable(),
  estimated_net_worth_range_min: z.number().int().nullable(),
  firstname: ZodTrimmedString.nullable(),
  flat_building: ZodTrimmedString.nullable(),
  formance_ledger_url: ZodTrimmedString,
  fsu: ZodFSUCases,
  funding_source: ZodTrimmedString.nullable(),
  gender: ZodTrimmedString.nullable(),
  has_documents: z.boolean(),
  identity_task_id: ZodId.nullable(),
  intercom_contact_id: ZodTrimmedString.nullable(),
  invest_range_max: z.number().nullable(),
  invest_range_min: z.number().nullable(),
  investment_horizon: ZodUserInvestmentHorizon.nullable(),
  investment_objectives: ZodUserInvestmentObjectives.nullable(),
  investment_experience_score_etf: ZodInvestmentExperienceScore.nullable(),
  investment_experience_score_stocks_bonds: ZodInvestmentExperienceScore.nullable(),
  investment_experience_score_commodities: ZodInvestmentExperienceScore.nullable(),
  investment_experience_score_structured_products: ZodInvestmentExperienceScore.nullable(),
  is_address_user_controlled: z.boolean(),
  is_ambassador: z.boolean(),
  is_email_user_controlled: z.boolean(),
  is_mailing_address_user_controlled: z.boolean(),
  is_phone_number_user_controlled: z.boolean(),
  is_politically_exposed_relative: z.boolean(),
  is_politically_exposed: z.boolean().nullable(),
  is_control_person: z.boolean().nullable(),
  is_shares_staff: z.boolean(),
  is_pfnl_dispense_eligible: z.boolean(),
  is_deceased: z.boolean(),
  date_of_decease: ZodDateTimeString.nullable(),
  kyc_state: ZodKycState,
  last_periodic_review_at: z.number().nullable(),
  next_periodic_review_at: z.number().nullable(),
  lastname: ZodTrimmedString.nullable(),
  linked_users: z.array(z.number()),
  mailing_city: ZodTrimmedString.nullable(),
  mailing_flat_building: ZodTrimmedString.nullable(),
  mailing_postalcode: ZodTrimmedString.nullable(),
  mailing_street: ZodTrimmedString.nullable(),
  mdlr_beneficiary_id: ZodId.nullable(),
  mdlr_customer_id: ZodTrimmedString.nullable(),
  mdlr_customer_url: ZodTrimmedString.nullable(),
  mdlr_verification_status: ZodMdlrVerificationStatus.nullable(),
  nationality: ZodTrimmedString.nullable(),
  nature_of_transactions: ZodNatureOfTransactions.nullable(),
  new_conversation_url: ZodTrimmedString.nullable(),
  onboarding_path: ZodOnboardingPath.nullable(),
  onboarding_risk_score: z.number().nullable(),
  onboarding_risk: ZodUserRisk.nullable(),
  onboarding_tax_number_state: ZodTrimmedString.nullable(),
  other_nationality: ZodTrimmedString.nullable(),
  phone_number: ZodPhoneNumber.nullable(),
  place_of_birth: ZodTrimmedString.nullable(),
  postalcode: ZodTrimmedString.nullable(),
  postcode_of_birth: ZodTrimmedString.nullable(),
  profile_pic: ZodTrimmedString.nullable(),
  reason_of_opening_account: ZodReasonOfOpeningAccount.nullable(),
  restrictions_flags: ZodRestrictionsFlags,
  risk_score: z.number().nullable(),
  risk: ZodUserRisk.nullable(),
  social_url: ZodTrimmedString.nullable(),
  sof_savings_limit: z.number().nullable(),
  sof_yearly_income_limit: z.number().nullable(),
  street: ZodTrimmedString.nullable(),
  total_assets_savings_range_min: z.number().int().nullable(),
  total_assets_savings_range_max: z.number().int().nullable(),
  tax_number: ZodTrimmedString.nullable(),
  transaction_reporting_id: ZodTrimmedString.nullable(),
  username: ZodTrimmedString,
  wealth_manager_id: ZodId.nullable(),
  wealth_manager_is_white_label: z.boolean().nullable(),
  edit_requests_cnt: z.number().int(),
  tasks_cnt: z.number().int(),
  moderation_tasks_cnt: z.number().int(),
  ml_alerts_cnt: z.number().int(),
  account_status: ZodUserAccountStatusBo,
  eckert_status: ZodUserEckertStatus,
  cto_id: ZodTrimmedString,
  b2b_flags: z.array(ZodB2bUserFlag),
  vulnerable_comment_choices: z.array(ZodVulnerableCommentChoice),
  tenant_id: ZodId,
  id_external_taiga: z.number().int().nullable(),
});

export type GetUserDetailsResponseI = z.TypeOf<typeof ZodGetUserDetailsResponse>;

export const getUsersEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetUsersRequestQuery,
  responseBody: ZodGetUsersResponse,
});

const ZodGetUsersIDPathParam = createZodHashIdParamsValidator(
  z.object({
    id: ZodHashedIdRequest.or(ZodId),
  }),
);

export type GetUsersEndpoint = GetEndpointType<typeof getUsersEndpoint>;

export const getUsersIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetUsersIDPathParam,
  responseBody: ZodGetUserDetailsResponse,
});

export type GetUsersIdEndpoint = GetEndpointType<typeof getUsersIdEndpoint>;

const ZodGetUserTenantsOneResponse = z.object({
  id: ZodId,
  displayName: ZodTrimmedString,
});
const ZodGetUserTenantsResponse = ZodGetUserTenantsOneResponse.array();
export type GetUserTenantsOneResponse = z.TypeOf<typeof ZodGetUserTenantsOneResponse>;

const ZodUserTenantsFilters = z
  .object({
    id: createZodArrayable(ZodId),
    displayName: ZodTrimmedString,
  })
  .partial();

const getUserTenantsSortColumns = ['displayName', 'id'];
const ZodGetUserTenantsQuery = buildZodBoListQueryParsed(
  ZodUserTenantsFilters,
  getUserTenantsSortColumns,
  {
    range: [0, 100],
  },
);

export type GetUserTenantsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodUserTenantsFilters>,
  (typeof getUserTenantsSortColumns)[number]
>;

export const getUserTenantsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetUserTenantsQuery,
  responseBody: ZodGetUserTenantsResponse,
});

export type GetUserTenantsEndpoint = GetEndpointType<typeof getUserTenantsEndpoint>;

export const getUserTenantsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetUserTenantsOneResponse,
});

export type GetUserTenantsIdEndpoint = GetEndpointType<typeof getUserTenantsIdEndpoint>;

export const EmailBlockLogResourceValues = [
  'user',
  'b2b_employee',
  'b2b_admin',
  'b2b_company',
  'b2b_contract',
] as const satisfies readonly BOLogResource[];
export type EmailBlockLogResource = (typeof EmailBlockLogResourceValues)[number];

export const postRemoveEmailBlockEndpoint = endpoint({
  method: 'POST',
  body: z.object({
    email: ZodTrimmedString,
    log_resource: z.object({ resource: z.enum(EmailBlockLogResourceValues), id: ZodId }).optional(),
  }),
  responseBody: z.object({ id: ZodId }),
});

export type PostRemoveEmailBlockEndpoint = GetEndpointType<typeof postRemoveEmailBlockEndpoint>;
